import React, { useContext } from 'react';
import styled from 'styled-components/macro';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import NextAction from '../Widget/NextAction';
import Overlay from '../Widget/Overlay';
import OverlayPartner from '../Widget/OverlayPartner';
import OverlayPublisher from '../Widget/OverlayPublisher';
import WidgetHeader from '../Widget/WidgetHeader';

import { Container } from '../DefaultStyles/Details';
import {
    Body,
    Content,
    Details,
    DetailsContainer,
    Footer,
    PeopleSigned,
    Title as QuestionText
} from '../DefaultStyles/Shared';

import { setExitingCardProps, getExitingCardProps } from '../Widget/actions';

import { fillAnswerPercentage } from './PollAnimations';
import { ImageBanner } from '../../components/Link/Components';
import { isLongText } from '../../components/Shared/commonFunctions';
import { TransitionContainerNoExit } from '../../components/Transition/TranstionContainer';
import ShareDrawer from '../../components/ShareDrawer';

import {
    getNextAction,
    buildConditionalActionsAnswerHistory
} from '../../utils/nextActionUtil';
import { formatEmoji, formatThousands } from '../../utils/fillDynamicText';
import { ActionTypes } from '../../utils/enums';
import { isPortrait } from '../../utils/size';

import { copyFor, capitalization } from '../../copy/copy';
import ThemeContext, { hexToRgb } from '../../utils/theming';

const Percentage = styled.div`
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: 50px;
    right: 12px;
    font-size: ${props =>
        props.noOfAnswers === 4 ? '18px' : props.noOfAnswers === 3 ? '15px' : '18px'};
    line-height: 1.22;
    font-weight: bold;
    text-align: right;

    @media (max-width: 35em) {
        font-size: 14px;
        right: 6px;
    }

    @media (max-width: 30em) {
        right: 12px;
    }
`;

const AnswerPercentage = styled.div`
    position: absolute;
    left: 0;
    top: 0;
    animation: ${({ percentage }) => fillAnswerPercentage(percentage)} 750ms ease-in;
    width: ${({ percentage }) => percentage}%;
    height: 100%;
    border-radius: 4px 0 0 4px;
    background: ${({ answerAnswered, theme }) => {
        const { r, g, b } = hexToRgb(theme.colors.primary);
        return answerAnswered
            ? `rgba(${r}, ${g}, ${b}, 0.5)`
            : `rgba(${r}, ${g}, ${b}, 0.2)`;
    }};
`;

const AnswerText = styled.div`
    position: absolute;
    left: 12px;
    top: 0;
    top: 50%;
    display: flex;
    justify-content: flex-start;
    align-items: center;
    transform: translateY(-50%);
    font-size: ${props =>
        props.noOfAnswers === 4 ? '14px' : props.noOfAnswers === 3 ? '13px' : '18px'};
    font-weight: bold;
    color: #000000;
    padding-right: ${props =>
        props.noOfAnswers === 4 ? '58px' : props.noOfAnswers === 3 ? '48px' : '58px'};
    line-height: ${props =>
        props.noOfAnswers === 4 ? '1.12' : props.noOfAnswers === 3 ? '1.15' : '1.18'};
    text-transform: ${props => (props.makeUppercase ? 'uppercase' : 'none')};

    @media (max-width: 35em) {
        font-size: ${props =>
            props.noOfAnswers === 4 ? '13px' : props.noOfAnswers === 3 ? '13px' : '15px'};
        padding-right: 38px;
        left: 6px;
    }

    @media (max-width: 30em) {
        font-size: 13px;
        padding-right: 44px;
        left: 12px;
        line-height: 1.1;
    }

    .emoji {
        font-size: 22px;
    }
`;

const AnswerContent = styled.div`
    width: ${props =>
        props.noOfAnswers === 4
            ? 'calc(50% - 16px)'
            : props.noOfAnswers === 3
            ? 'calc(33.33% - 16px)'
            : 'calc(50% - 16px)'};
    height: ${props => (props.noOfAnswers === 4 ? 55 : 88)}px;
    margin: 0 8px;
    margin-top: ${props => (props.noOfAnswers === 4 ? 10 : 0)}px;
    line-height: 100%;
    float: left;
    position: relative;
    border-radius: 4px;
    border: solid 1px #f1f1ee;

    @media (max-width: 30em) {
        width: calc(100% - 15px);
        height: ${props => (props.noOfAnswers >= 3 && props.hasNextAction ? 51 : 70)}px;
        margin-top: 10px;
    }
`;

const AnswersWrapper = styled.div`
    float: left;
    flex: 1;
    display: flex;
    height: 100%;
    align-items: center;
    ${props => props.hasNextAction && `margin-top: -40px;`};
    ${() =>
        isPortrait() &&
        `
            flex-direction: column;
            justify-content: center;
        `}
    ${props =>
        props.hasNextAction && isPortrait() && props.isVisualAction && `margin-top: 0px;`}
`;

const PollSuccess = props => {
    const theme = useContext(ThemeContext);
    return (
        <TransitionContainerNoExit zIndex={15}>
            <Container
                desktopLinkImage={
                    !!props.desktopImageId && props.isVisualAction && !isPortrait()
                }
            >
                <Overlay />
                <OverlayPublisher />
                <OverlayPartner transitionType="right-to-left" />
                <Body className="body-msn-override" isBlurred={false}>
                    <WidgetHeader />
                    <Content
                        flexGrow={true}
                        isContactAction={false}
                        useLocalRepresentative={false}
                        isSingleAction={true}
                        isVisualAction={props.isVisualAction}
                    >
                        <QuestionText
                            isShortText={!isLongText(props.questionText, 28)}
                            isLongText={isLongText(props.questionText, 60)}
                            isExtraLongText={isLongText(props.questionText, 95)}
                            dangerouslySetInnerHTML={{
                                __html: props.questionText
                            }}
                        />
                        <DetailsContainer flexStart>
                            <Details style={{ position: `relative`, height: '100%' }}>
                                {(!props.hasNextAction || props.isMobileResolution) && (
                                    <PeopleSigned style={{ margin: 0 }}>
                                        {props.noOfPeopleAnswered}{' '}
                                        {copyFor(
                                            'miscellaneous',
                                            'numAnswered',
                                            'poll',
                                            capitalization.all
                                        )}
                                    </PeopleSigned>
                                )}
                                <AnswersWrapper
                                    numAnswers={props.results.length}
                                    hasNextAction={props.hasNextAction}
                                    isVisualAction={props.isVisualAction}
                                >
                                    {props.results.map(r => (
                                        <AnswerContent
                                            key={r.id}
                                            hasNextAction={props.hasNextAction}
                                            noOfAnswers={r.noOfAnswers}
                                        >
                                            <AnswerPercentage
                                                theme={theme}
                                                percentage={r.percentage}
                                                answerAnswered={r.answerAnswered}
                                            />
                                            <AnswerText
                                                theme={theme}
                                                noOfAnswers={r.noOfAnswers}
                                                makeUppercase={props.makeUppercase}
                                                dangerouslySetInnerHTML={{
                                                    __html: formatEmoji(r.text)
                                                }}
                                            />
                                            <Percentage noOfAnswers={r.noOfAnswers}>
                                                {r.percentage}%
                                            </Percentage>
                                        </AnswerContent>
                                    ))}
                                </AnswersWrapper>
                            </Details>
                        </DetailsContainer>
                    </Content>
                    <NextAction
                        isVisual={props.isVisualAction}
                        beforeAdvancing={() => {
                            props.setExitingCardProps('poll', props, props.activeAction);
                        }}
                        exitingAction={props.activeAction}
                    />
                    {props.isVisualAction && (
                        <ImageBanner
                            desktopImageId={props.desktopImageId}
                            mobileImageId={props.mobileImageId}
                        />
                    )}
                    <Footer>
                        <ShareDrawer newsletterSignUpVisible={true} />
                    </Footer>
                </Body>
            </Container>
        </TransitionContainerNoExit>
    );
};

PollSuccess.propTypes = {
    activeAction: PropTypes.object.isRequired,
    desktopImageId: PropTypes.string,
    hasNextAction: PropTypes.bool,
    isMobileResolution: PropTypes.bool,
    isVisualAction: PropTypes.bool,
    makeUppercase: PropTypes.bool.isRequired,
    mobileImageId: PropTypes.string,
    noOfPeopleAnswered: PropTypes.string.isRequired,
    questionText: PropTypes.string.isRequired,
    results: PropTypes.array.isRequired,
    setExitingCardProps: PropTypes.func.isRequired
};

export default connect(
    (state, props) => {
        const exitingProps = getExitingCardProps(
            'poll',
            ['/poll/success'],
            props.location.pathname,
            state
        );
        if (exitingProps) return exitingProps;

        return {
            activeAction: state.widget.activeAction,
            questionText:
                state.poll.successInfo !== undefined
                    ? state.poll.successInfo.questionText
                    : 'Thanks for answering!',
            isMobileResolution: state.widget.isMobileResolution,
            isVisualAction: state.widget.activeAction.isVisual,
            desktopImageId: state.widget.activeAction.desktopImageId,
            mobileImageId: state.widget.activeAction.mobileImageId,
            hasNextAction: !!getNextAction(
                state.widget.actions,
                state.widget.activeAction.identifier,
                buildConditionalActionsAnswerHistory(state),
                state.widget.cards
            ),
            results:
                state.poll.successInfo.answers != null
                    ? state.poll.successInfo.answers
                          .filter(a => a.isOpen === false)
                          .map(p => ({
                              ...p,
                              percentage:
                                  state.poll.answerHistory !== undefined
                                      ? state.poll.answerHistory.find(
                                            r =>
                                                r.actionIdentifier ===
                                                state.poll.successInfo.actionIdentifier
                                        ) !== undefined
                                          ? state.poll.answerHistory
                                                .find(
                                                    r =>
                                                        r.actionIdentifier ===
                                                        state.poll.successInfo
                                                            .actionIdentifier
                                                )
                                                .results.find(
                                                    r => r.answerId === p.id
                                                ) !== undefined
                                              ? state.poll.answerHistory
                                                    .find(
                                                        r =>
                                                            r.actionIdentifier ===
                                                            state.poll.successInfo
                                                                .actionIdentifier
                                                    )
                                                    .results.find(
                                                        r => r.answerId === p.id
                                                    ).percentage
                                              : 0
                                          : 0
                                      : 0,
                              answerAnswered:
                                  state.poll.answerHistoryClicked !== undefined
                                      ? state.poll.answerHistoryClicked.find(
                                            r =>
                                                r.actionIdentifier ===
                                                state.poll.successInfo.actionIdentifier
                                        ) !== undefined
                                          ? state.poll.answerHistoryClicked
                                                .filter(
                                                    r =>
                                                        r.actionIdentifier ===
                                                        state.poll.successInfo
                                                            .actionIdentifier
                                                )
                                                .slice(-1)[0].answerId === p.id
                                          : state.poll.answerHistory !== undefined
                                          ? state.poll.answerHistory.find(
                                                r =>
                                                    r.actionIdentifier ===
                                                    state.poll.successInfo
                                                        .actionIdentifier
                                            ) !== undefined
                                              ? state.poll.answerHistory.find(
                                                    r =>
                                                        r.actionIdentifier ===
                                                        state.poll.successInfo
                                                            .actionIdentifier
                                                ).answerId === p.id
                                              : false
                                          : false
                                      : state.poll.answerHistory !== undefined
                                      ? state.poll.answerHistory.find(
                                            r =>
                                                r.actionIdentifier ===
                                                state.poll.successInfo.actionIdentifier
                                        ) !== undefined
                                          ? state.poll.answerHistory.find(
                                                r =>
                                                    r.actionIdentifier ===
                                                    state.poll.successInfo
                                                        .actionIdentifier
                                            ).answerId === p.id
                                          : false
                                      : false,
                              noOfAnswers: state.poll.successInfo.answers.filter(
                                  a => a.isOpen === false
                              ).length,
                              identifier: state.poll.successInfo.actionIdentifier
                          }))
                    : [],
            noOfPeopleAnswered: state.poll.successInfo.noOfAnswers
                ? formatThousands(state.poll.successInfo.noOfAnswers + 1)
                : '',
            makeUppercase: (state.poll.successInfo.answers || []).every(
                a => !a.isOpen && a.text.length <= 8
            )
        };
    },
    { setExitingCardProps },
    null,
    {
        areStatesEqual: (next, prev) => {
            if (next.widget.activeAction.type !== ActionTypes.Poll) return true;
            return next === prev;
        }
    }
)(PollSuccess);
